import React, { useEffect, useState } from "react";
import api from "../../api";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import ChartInfoPopup from "./ChartInfoPopup";

ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);

const TokenUsageChart = () => {
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const [themeColors, setThemeColors] = useState({});

  // read css variables
  const loadThemeColors = () => {
    const styles = getComputedStyle(document.documentElement);
    return {
      text: styles.getPropertyValue("--dark-color").trim(),
      grid: styles.getPropertyValue("--border-color").trim(),
      bg: styles.getPropertyValue("--background-color").trim(),
    };
  };

  useEffect(() => {
    setThemeColors(loadThemeColors());

    const observer = new MutationObserver(() => {
      setThemeColors(loadThemeColors());
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  // format bytes helper (same units logic as backend)
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return "0 KB";
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const value = bytes / Math.pow(1024, i);
    const fixed = parseFloat(value.toFixed(2));
    return `${fixed} ${sizes[i]}`;
  };

  useEffect(() => {
    const fetchMonthlyUsage = async () => {
      try {
        const token =
          localStorage.getItem("access_token") ||
          sessionStorage.getItem("access_token");

        const baseUrl = process.env.REACT_APP_API_URL;

        const res = await api.get(`${baseUrl}/chat/monthly-usage`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const monthlyStats = res.data.monthlyStats || [];
        const labels = monthlyStats.map((m) => m.month);

        const storageValues = monthlyStats.map((m) => m.rawStorageUsed ?? 0);
        const totalStorage = storageValues.reduce((a, b) => a + b, 0);

        // build chart datasets (use rawStorageUsed numeric for chart)
        setChartData({
          labels,
          datasets: [
            {
              label: "Token Usage",
              data: monthlyStats.map((m) => m.tokenUsage || 0),
              borderColor: "#3b82f6",
              backgroundColor: "#3b82f6",
              tension: 0,
              pointRadius: 4,
              pointHoverRadius: 6,
              fill: false,
            },
            {
              label: "Number of Conversations",
              data: monthlyStats.map((m) => m.conversationCount || 0),
              borderColor: themeColors.text || "#000",
              backgroundColor: themeColors.text || "#000",
              tension: 0,
              pointRadius: 4,
              pointHoverRadius: 6,
              fill: false,
            },
            {
              label: `Storage Used (${formatBytes(totalStorage)})`, // 🎉 Updated Label with formatted total
              data: storageValues,
              borderColor: "#8b5cf6",
              backgroundColor: "#8b5cf6",
              tension: 0,
              pointRadius: 4,
              pointHoverRadius: 6,
              fill: false,
            },
          ],
        });
      } catch (err) {
        console.error("Failed to fetch monthly usage", err);
      }
    };

    fetchMonthlyUsage();
  }, [themeColors]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        align: "start",
        labels: {
          color: themeColors.text,
          usePointStyle: true,
          pointStyle: "line",
          font: { size: 13, weight: "500" },
        },
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem) => {
            const dsIndex = tooltipItem.datasetIndex;
            const value = tooltipItem.raw ?? tooltipItem.parsed?.y;
            if (dsIndex === 2) {
              // Storage dataset -> show formatted
              return `Storage: ${formatBytes(Number(value || 0))}`;
            }
            // others -> show raw number
            const label = tooltipItem.dataset.label || "";
            return `${label}: ${value}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { color: themeColors.text },
        grid: { color: themeColors.grid },

        // 👉 Y-Axis Label
        title: {
          display: true,
          text: "Usage Metrics",
          color: themeColors.text,
          font: {
            size: 13,
            weight: "bold",
          },
        },
      },
      x: {
        ticks: { color: themeColors.text },
        grid: { color: themeColors.grid },

        // 👉 X-Axis Label
        title: {
          display: true,
          text: "Time Period",
          color: themeColors.text,
          font: {
            size: 13,
            weight: "bold",
          },
          padding: { top: 10 },
        },
      },
    },
  };

  return (
    <div className="chart-card p-3 shadow-sm rounded mb-4  d-flex flex-column">
      <div className="d-flex align-items-center gap-2 mb-2">
        <h4 className="fw-bold mb-0">
          <span className="border-start border-4 ps-2 ak-text-lg d-flex align-content-center refresh-prompts">
            Token Usage Over Time
          </span>
        </h4>

        <div className="chart-info-anchor">
          <ChartInfoPopup text="This chart provides a monthly view of your chatbot’s key usage metrics across the year - Token Usage, Number of Conversations, and Storage Used. The Y-axis reflecting the chosen metric and the X-axis showing months from January to December. It helps you monitor monthly trends, track growth, and compare how each metric changes throughout the year." />
        </div>
      </div>
      <p
        className="fw-bold refresh-prompts ak-text-md"
        style={{ marginBottom: "-1px" }}
      >
        Monthly Usage
      </p>

      <div style={{ height: "320px", width: "100%", marginTop: "auto" }}>
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

export default TokenUsageChart;
